import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Chip,
    Divider,
} from "@mui/material";
import { Localized } from "@fluent/react";
import {
    DocumentRelation,
    DocumentRelationsOverview,
    DocumentRelationType,
    loadDocumentRelationsOverview,
    RelationDirectionType,
} from "../documentsApi";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../app/hooks";
import { unwrapResult } from "@reduxjs/toolkit";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { ErrorContainer } from "../../../app/ErrorContainer";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getLink } from "./DocumentLinkProvider";
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

export const DocumentRelationsPanel = ({ documentId }: { documentId: string }) => {
    const dispatch = useAppDispatch();

    const [data, setData] = useState<DocumentRelationsOverview | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [error, setError] = useState<any>(null);

    const load = useCallback(() => {
        setIsLoading(true);
        setIsError(false);
        setError(null);

        return dispatch(loadDocumentRelationsOverview(documentId))
            .then(unwrapResult)
            .then(setData)
            .catch((e) => {
                console.error(e);
                setIsError(true);
                setError(e);
            })
            .finally(() => setIsLoading(false));
    }, [dispatch, documentId]);

    useEffect(() => {
        load();
    }, [load]);

    const outgoing = useMemo(() => data?.outgoing ?? [], [data]);
    const incoming = useMemo(() => data?.incoming ?? [], [data]);

    if (isLoading) return <LoadingContainer />;
    if (isError) return <ErrorContainer error={error} />;

    return (
        <Paper elevation={3} sx={{ mb: 1, overflow: "hidden" }}>
            <Box
                sx={{
                    px: 2,
                    py: 0.75,
                    bgcolor: "action.hover",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    <Localized id="document-relations">_Relacje dokumentu</Localized>
                </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
                <Section
                    titleId="document-relations-outgoing"
                    items={outgoing}
                    direction="OUTGOING"
                />

                <Divider sx={{ my: 2 }} />

                <Section
                    titleId="document-relations-incoming"
                    items={incoming}
                    direction="INCOMING"
                />
            </Box>
        </Paper>
    );
};

type UiMeta = {
    labelId: string;
    icon: React.ReactNode;
};


export const getRelationMeta = (
    type: DocumentRelationType,
    direction: RelationDirectionType
): UiMeta => {
    if (direction === "OUTGOING") {
        switch (type) {
            case "TEMPLATE":
                return { labelId: "document-relations-created-from-template", icon: <DescriptionOutlinedIcon fontSize="small" /> };
            case "BASED_ON":
                return { labelId: "document-relations-based-on", icon: <ArticleOutlinedIcon fontSize="small" /> };
            case "REPORT":
                return { labelId: "document-relations-report-of", icon: <AssignmentOutlinedIcon fontSize="small" /> };
            case "REQUIREMENT_PACKAGE":
                return { labelId: "document-relations-requirement-package-of", icon: <AssignmentOutlinedIcon fontSize="small" /> };
        }
    }

    switch (type) {
        case "TEMPLATE":
            return { labelId: "document-relations-template-for", icon: <DescriptionOutlinedIcon fontSize="small" /> };
        case "BASED_ON":
            return { labelId: "document-relations-base-for", icon: <ArticleOutlinedIcon fontSize="small" /> };
        case "REPORT":
            return { labelId: "document-relations-has-report", icon: <AssignmentOutlinedIcon fontSize="small" /> };
        case "REQUIREMENT_PACKAGE":
            return { labelId: "document-relations-requirement-package-for", icon: <AssignmentOutlinedIcon fontSize="small" /> };
        default:
            return { labelId: "document-relations-unknown", icon: <ArticleOutlinedIcon fontSize="small" /> };

    }

};

const SectionDirectionIcon = ({ direction }: { direction: RelationDirectionType }) => {
    const isOutgoing = direction === "OUTGOING";

    return isOutgoing ? (
        <ArrowRightIcon sx={{ fontSize: 18, opacity: 0.7 }} />
    ) : (
        <ArrowLeftIcon sx={{ fontSize: 18, opacity: 0.7 }} />
    );
};

type SectionProps = {
    titleId: string;
    items: DocumentRelation[];
    direction: RelationDirectionType;
};

const Section = ({ titleId, items, direction }: SectionProps) => {
    const navigate = useNavigate();

    return (
        <>
            <Box
                sx={{
                    px: 1,
                    py: 0.75,
                    bgcolor: "grey.100",
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <SectionDirectionIcon direction={direction} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>

                    <Localized id={titleId}>_</Localized>
                </Typography>
            </Box>

            {items.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7, px: 1 }}>
                    <Localized id="document-relations-none">_Brak relacji</Localized>
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 220 }}>
                                    <Localized id="document-relations-column-relation-type">_Typ relacji</Localized>
                                </TableCell>
                                <TableCell sx={{ width: 160 }}>
                                    <Localized id="document-type">_Typ dokumentu</Localized>
                                </TableCell>
                                <TableCell sx={{ width: 160 }}>
                                    <Localized id="number">_Numer</Localized>
                                </TableCell>
                                <TableCell>
                                    <Localized id="title">_Tytuł</Localized>
                                </TableCell>
                                <TableCell align="right" sx={{ width: 120 }} />
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {items.map((r) => {
                                const meta = getRelationMeta(r.relationType, r.direction);

                                return (
                                    <TableRow key={String(r.id)} hover>
                                        <TableCell>
                                            <Box
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    px: 1,
                                                    py: 0.5,
                                                    bgcolor: "grey.100",
                                                    borderRadius: 1,
                                                }}
                                            >
                                                {meta.icon}
                                                <Typography variant="caption">
                                                    <Localized id={meta.labelId}>_</Localized>
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {String(r.relatedDocumentType)}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {r.relatedDocumentNumber}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {r.relatedDocumentTitle}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="text"
                                                endIcon={<OpenInNewIcon fontSize="small" />}
                                                onClick={() => navigate(getLink(r.relatedDocumentType, r.relatedDocumentId))}
                                            >
                                                <Localized id="open">_Otwórz</Localized>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    );
};
